/**
 * 404 page.
 *
 * The router's catch-all used to silently `<Navigate to="/" />` on any
 * unmatched path — a typo'd URL, a stale bookmark, a link to a transaction
 * that got deleted all landed back on the login/landing screen with zero
 * explanation, indistinguishable from a session timeout. This says what
 * actually happened and offers a way out.
 */
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Button,
  Center,
  Heading,
  Text,
  VStack,
  Code,
} from '@chakra-ui/react';
import { useAuth } from '../hooks/useAuth';
import { useUserProfile } from '../hooks/useUserProfile';

export const NotFoundPage: React.FC = () => {
  const { user } = useAuth();
  const { isAdmin } = useUserProfile();

  // Route "go home" to wherever this user actually lands post-login, rather
  // than always sending them to "/" — an authenticated admin bounced to the
  // public landing page would just read as another broken link.
  const homePath = !user ? '/' : isAdmin ? '/admin' : '/home';

  return (
    <Center minH="100vh" bg="bg.primary" px={4}>
      <VStack spacing={4} maxW="md" textAlign="center">
        <Code fontSize="5xl" px={3} py={1} bg="transparent" color="accent.primary">
          404
        </Code>
        <Heading size="lg" color="text.primary">
          Page not found
        </Heading>
        <Text color="text.secondary">
          The page you're looking for doesn't exist or may have been moved.
        </Text>
        <Button as={RouterLink} to={homePath} colorScheme="blue">
          {user ? 'Back to dashboard' : 'Back to login'}
        </Button>
      </VStack>
    </Center>
  );
};
